import { z } from "zod";

import { ParameterPolicySchema } from "./parameterPolicy.schema";

/**
 * Policy for a single action within an Access Card.
 *
 * Defines how parameters are exposed when this action is invoked through the card.
 * Parameters not listed are disabled (stripped from the API call) by default.
 *
 * The map is FLAT and its keys are `ParameterPath`s — `to`, `body.html`,
 * `body.attachments[*].filename`. There is no nested `children` shape, and that is
 * what makes the whole widening backward compatible: a key with no `.` and no `[*]`
 * is a plain top-level parameter name and is enforced exactly as it always was.
 *
 * The key stays `z.string()` rather than `ParameterPathSchema` on purpose. Parameter
 * names come from upstream catalogues, so a stored card can hold a key the path
 * grammar cannot spell. Tightening the key here would make that card fail to parse —
 * it would stop being readable at all, which is an availability incident and not a
 * security gain. The grammar is enforced where it does something instead: the strict
 * engine REFUSES a non-addressable path, and `validateCardPolicies` reports it at
 * save time.
 */
export const ActionPolicySchema = z.object({
  /** Per-parameter overrides. Key = `ParameterPath`, value = policy. */
  parameters: z.record(z.string(), ParameterPolicySchema).optional()
});

export type ActionPolicy = z.infer<typeof ActionPolicySchema>;
